import type { ReactNode } from 'react'

/** 一个页签的元信息 */
export type WorkflowTab = {
  id: string
  /** 页签标题，越短越好（一行放得下 2-3 个） */
  title: string
  /** 悬停 / 无障碍补充说明 */
  desc?: string
}

/**
 * 生成页的页签容器 —— **取代原来的分步向导（Stepper + StepNav）。**
 *
 * 2026-09-28 用户驳回三步骨架，原话：「不需要准备素材」「这个应该 2-3 tab」。
 * 分步向导的三个真问题：
 *   1. 「准备素材」这一步的字段（产品名 / 卖点 / 参考图）本来就该跟着配置一起看，
 *      拆出来只让人多点一次「下一步」；
 *   2. 底部「上一步 / 下一步」把可提交状态藏起来了 —— 用户在第 1 步看不到价格，
 *      在第 2 步看不到生成按钮，得先"走完流程"才知道要花多少钱；
 *   3. 页签能横向比较、能直接跳，比线性向导更符合"我知道自己要改哪一项"的场景。
 *
 * 现在：顶部一行页签（可任意点）、内容区。**提交栏不在这个组件里** ——
 * 它 2026-10-08 起挂在右栏顶部的 `.studio__dock`（用户口径「每一个页面的
 * [生成按钮] 都放在右上角，[价格] 为同一行」），因此本组件曾经有的 `footer`
 * 底部槽**整个下线**：9 个页面都不再往页签底部挂东西，留着那个槽就是留着
 * 一个「谁都能再塞一个提交栏进去」的位置（那就又是两份提交栏）。
 *
 * 校验不过时给出「还差什么」而不是禁用整个按钮不留话（那句话跟着提交栏走，
 * 现在显示在右栏顶部按钮的下方）。
 *
 * 受控组件：当前页签由父组件持有 —— 各页的必填项与提交条件不同，
 * 放在父组件才不会把校验规则硬编码进组件。
 */
export function WorkflowTabs({
  tabs,
  current,
  onChange,
  children,
}: {
  tabs: WorkflowTab[]
  current: number
  onChange: (index: number) => void
  children: ReactNode
}) {
  return (
    <div className="wf-tabs">
      <div className="wf-tabs__bar" role="tablist" aria-label="配置页签">
        {tabs.map((t, i) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`wf-tab-${t.id}`}
            aria-selected={i === current}
            aria-controls={`wf-panel-${t.id}`}
            className="wf-tabs__tab"
            data-active={i === current}
            title={t.desc ?? t.title}
            onClick={() => onChange(i)}
          >
            <span className="wf-tabs__num">{i + 1}</span>
            <span className="wf-tabs__label">{t.title}</span>
          </button>
        ))}
      </div>

      <div className="wf-tabs__body" role="tabpanel" id={`wf-panel-${tabs[current]?.id ?? ''}`}>
        {children}
      </div>
    </div>
  )
}
